import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import moment from "moment";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import LinearProgress from "@mui/material/LinearProgress";
import CircularProgress from "@mui/material/CircularProgress";
import Backdrop from "@mui/material/Backdrop";
import CloseIcon from "@mui/icons-material/Close";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import RotateLeftOutlinedIcon from "@mui/icons-material/RotateLeftOutlined";
import InnerImageZoom from "react-inner-image-zoom";
import "react-inner-image-zoom/lib/styles.min.css";

import { getDegree, getImageDegree, rotateImage } from "../../utils/images";
import ConfirmDeleteModal from "./ConfirmDeleteModal";
import UploadFileButton from "../UploadFileButton";
import ImageCounter from "./ImageCounter";
import { momentFormat, serverFormatWithTimezone } from "../../utils/dates";
import { isPdf } from "../../utils/fileTypes";

const DisableTransitionComponent = ({ children }) => children;

const paperBase = {
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  height: "calc(100vh - 48px)",
  m: 3,
};

const styles = {
  paper: { ...paperBase, maxWidth: 1000, width: "90vw" },
  pdfPaper: { ...paperBase, maxWidth: 1200, width: "55vw" },
  backdrop: {
    transition: "none !important",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    px: 2,
    py: 1,
    borderBottom: 1,
    borderColor: "divider",
    flexShrink: 0,
    minHeight: 48,
    gap: 1,
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: 0.5,
    flexShrink: 0,
  },
  headerAction: {
    textTransform: "none",
    fontSize: 14,
    fontWeight: 500,
    color: "text.secondary",
    minWidth: 0,
    px: 1,
  },
  content: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    position: "relative",
    overflow: "hidden",
    minHeight: 0,
    bgcolor: "grey.50",
  },
  imageArea: {
    flex: 1,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
    overflow: "hidden",
    position: "relative",
    "& .iiz": {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    },
    "& .iiz__img": {
      maxHeight: "calc(100vh - 200px)",
      maxWidth: "100%",
      objectFit: "contain",
    },
  },
  navButton: {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    zIndex: 2,
    color: "text.primary",
    bgcolor: "rgba(255, 255, 255, 0.85)",
    boxShadow: 1,
    width: 36,
    height: 36,
    "&:hover": { bgcolor: "rgba(255, 255, 255, 0.95)" },
  },
  footer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    px: 2,
    py: 1.5,
    borderTop: 1,
    borderColor: "divider",
    flexShrink: 0,
  },
  pdfIframe: {
    width: "100%",
    height: "100%",
    border: "none",
    borderRadius: 4,
  },
};

const ImageModal = ({
  open,
  edit = true,
  images,
  asset,
  closeOnSave = false,
  selectedId: defaultId,
  onClose,
  onRemove,
  onUpdate,
  onCreate,
  displaySetAsDefault = true,
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleteImage, setDeleteImage] = useState(false);
  const [selectedId, setSelectedId] = useState(defaultId);
  const [anchorEl, setAnchorEl] = useState(null);
  const [rotateAnchorEl, setRotateAnchorEl] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);

  const currentImageIndex = Math.max(
    0,
    images.findIndex((image) => selectedId === image.id)
  );
  const currentImage = images[currentImageIndex];
  const currentIsPdf = isPdf(currentImage);
  const canRemove = edit && typeof onRemove === "function";
  const canUpdate = edit && typeof onUpdate === "function";
  const canCreate = edit && typeof onCreate === "function";
  const capturedOn = currentImage?.createdAt
    ? moment(currentImage.createdAt, serverFormatWithTimezone).format(momentFormat)
    : null;
  const InitialRotation = getImageDegree(currentImage?.url);
  const [defaultImage, setDefaultImage] = useState(currentImage?.default);
  const [rotate, setRotate] = useState(InitialRotation);

  const changed = InitialRotation !== rotate || currentImage?.default !== defaultImage;

  const prevImage = images[currentImageIndex - 1];
  const nextImage = images[currentImageIndex + 1];

  const imgUrl = rotateImage(currentImage?.url, "width", 1024, rotate);

  useEffect(() => {
    setImageLoaded(false);
  }, [currentImage?.id]);

  useEffect(() => {
    if (!imgUrl || currentIsPdf) {
      setImageLoaded(true);
      return;
    }
    const img = new Image();
    img.onload = () => setImageLoaded(true);
    img.onerror = () => setImageLoaded(true);
    img.src = imgUrl;
    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [imgUrl, currentIsPdf]);

  useEffect(() => {
    if (currentImage?.id) {
      setDefaultImage(currentImage?.default);
      setRotate(getImageDegree(currentImage?.url));
    }
  }, [currentImage?.id, currentImage?.default]);

  useEffect(() => {
    setSelectedId(defaultId);
  }, [defaultId]);

  const handleToggleModal = () => setDeleteImage((value) => !value);

  const handleRotate = (value) => {
    let newRotate = 0;
    if (rotate + value < 0) {
      newRotate = 360 + rotate + value;
    } else if (rotate + value >= 360) {
      newRotate = 0;
    } else {
      newRotate = rotate + value;
    }
    setRotate(newRotate);
  };

  const handleRemove = () => {
    setLoading(true);
    onRemove(
      {
        variables: { id: currentImage?.id },
        onSuccess: () => {
          if (images.length <= 1) onClose();
          setLoading(false);
          setSelectedId(prevImage?.id || nextImage?.id);
          closeOnSave && onClose();
        },
        onFailure: () => setLoading(false),
      },
      currentImage
    );
  };

  const handleSave = () => {
    setLoading(true);
    onUpdate(
      {
        variables: {
          id: currentImage?.id,
          url: getDegree(currentImage?.url, rotate),
          default: defaultImage,
        },
        onSuccess: () => {
          setLoading(false);
          closeOnSave && onClose();
        },
        onFailure: () => setLoading(false),
      },
      currentImage
    );
  };

  const handleUpload = (files) => {
    setUploading(true);
    onCreate({
      variables: { images: files },
      onSuccess: () => {
        setUploading(false);
        closeOnSave && onClose();
      },
      onFailure: () => setUploading(false),
    });
  };

  const handleClose = () => {
    if (loading || uploading) return;
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        TransitionComponent={DisableTransitionComponent}
        maxWidth={false}
        PaperProps={{ sx: currentIsPdf ? styles.pdfPaper : styles.paper }}
        slots={{ backdrop: Backdrop }}
        slotProps={{ backdrop: { sx: styles.backdrop } }}
      >
        <Box sx={styles.header}>
          <Typography variant="body2" color="text.secondary" noWrap>
            Captured On: {capturedOn}
          </Typography>
          <Box sx={styles.headerActions}>
            {canUpdate && !currentIsPdf && (
              <>
                <Button
                  size="small"
                  startIcon={<RotateLeftOutlinedIcon />}
                  onClick={(e) => setRotateAnchorEl(e.currentTarget)}
                  sx={styles.headerAction}
                >
                  Rotate
                </Button>
                <Menu
                  anchorEl={rotateAnchorEl}
                  open={Boolean(rotateAnchorEl)}
                  onClose={() => setRotateAnchorEl(null)}
                >
                  <MenuItem
                    onClick={() => {
                      handleRotate(-90);
                      setRotateAnchorEl(null);
                    }}
                  >
                    Rotate Left
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      handleRotate(90);
                      setRotateAnchorEl(null);
                    }}
                  >
                    Rotate Right
                  </MenuItem>
                </Menu>
              </>
            )}
            {edit && !currentIsPdf && (
              <>
                <Button
                  size="small"
                  startIcon={<MoreVertIcon />}
                  onClick={(e) => setAnchorEl(e.currentTarget)}
                  sx={styles.headerAction}
                >
                  More
                </Button>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={() => setAnchorEl(null)}
                >
                  {displaySetAsDefault && canUpdate && (
                    <MenuItem>
                      <Checkbox
                        checked={defaultImage}
                        onChange={(event) => setDefaultImage(Boolean(event.target.checked))}
                        color="primary"
                      />
                      <Typography variant="body2">Set As Default</Typography>
                    </MenuItem>
                  )}
                  <MenuItem
                    onClick={() => {
                      window.open(currentImage?.url, "_blank");
                      setAnchorEl(null);
                    }}
                  >
                    Download Image
                  </MenuItem>
                  <MenuItem onClick={() => navigate("/activity")}>Replay Log Hub</MenuItem>
                </Menu>
              </>
            )}
            <IconButton
              aria-label="close"
              disabled={loading || uploading}
              onClick={onClose}
              size="small"
              edge="end"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <Box sx={styles.content}>
          {prevImage && (
            <IconButton
              disabled={loading}
              onClick={() => setSelectedId(prevImage.id)}
              sx={{ ...styles.navButton, left: 12 }}
            >
              <NavigateBeforeIcon />
            </IconButton>
          )}

          <Box sx={styles.imageArea}>
            <ImageCounter index={currentImageIndex} total={images.length} />
            {currentIsPdf ? (
              <iframe
                src={currentImage.url}
                style={styles.pdfIframe}
                title={currentImage.fileName || "PDF Document"}
              />
            ) : !imageLoaded ? (
              <CircularProgress size={40} sx={{ color: "text.disabled" }} />
            ) : (
              <InnerImageZoom src={imgUrl} zoomSrc={imgUrl} zoomScale={2} />
            )}
          </Box>

          {nextImage && (
            <IconButton
              disabled={loading}
              onClick={() => setSelectedId(nextImage.id)}
              sx={{ ...styles.navButton, right: 12 }}
            >
              <NavigateNextIcon />
            </IconButton>
          )}
        </Box>

        {uploading && <LinearProgress />}

        {(canRemove || canCreate || canUpdate) && (
          <Box sx={styles.footer}>
            {canRemove && (
              <Button
                disabled={loading || images.length === 0}
                data-id="delete-image"
                onClick={handleToggleModal}
                variant="outlined"
                color="error"
                size="small"
              >
                Delete
              </Button>
            )}
            <Box display="flex" gap={1} ml="auto">
              {canCreate && (
                <UploadFileButton
                  multiple
                  name="imageModalUpload"
                  disabled={loading || uploading}
                  onUpload={handleUpload}
                  variant="outlined"
                  color="primary"
                  size="small"
                  accept="image/*"
                >
                  Upload
                </UploadFileButton>
              )}
              {canUpdate && (
                <Button
                  disabled={loading || !changed}
                  data-id="save-image"
                  onClick={handleSave}
                  variant="contained"
                  size="small"
                >
                  Save
                </Button>
              )}
            </Box>
          </Box>
        )}
      </Dialog>
      {deleteImage && (
        <ConfirmDeleteModal
          title="Delete Image"
          descr="Are you sure you want to delete image?"
          onClose={handleToggleModal}
          onDelete={handleRemove}
        />
      )}
    </>
  );
};

export default ImageModal;
